import { TandemTool } from "../../base/TandemTool.js";
import { logger } from "../../../../../../core/logger.js";

export class DuplicateFileTool extends TandemTool {
  constructor() {
    super();
    this.toolName = "duplicateFileTool";
  }

  /**
   * @param {string} sourcePath
   * @param {string} destinationPath
   * @param {boolean} [overwrite=false]
   * @returns {Promise<Object>}
   */
  async duplicateFile(sourcePath, destinationPath, overwrite = false) {
    try {
      if (!sourcePath || typeof sourcePath !== "string") {
        return this.returnError({}, "Valid source path required");
      }
      if (!destinationPath || typeof destinationPath !== "string") {
        return this.returnError({}, "Valid destination path required");
      }

      const api = this.getTandemLocalAppApi();
      if (!api?.duplicatePath) {
        return this.returnError(
          {},
          "Tandem duplicate requires Revolt Desktop (tandemAPI.duplicatePath)."
        );
      }

      const root = this.getTandemWorkingDirectoryRoot();
      if (!root) {
        return this.returnError(
          {},
          "Tandem has no project folder set (working directory path)."
        );
      }

      const normalizedSourcePath = this.normalizeAndValidatePath(sourcePath);
      const normalizedDestPath = this.normalizeAndValidatePath(destinationPath);
      if (!normalizedSourcePath || !normalizedDestPath) {
        return this.returnError({}, "Invalid source or destination path");
      }

      const sourceRelativePath = normalizedSourcePath.replace(/^\/+/, "");
      const destRelativePath = normalizedDestPath.replace(/^\/+/, "");
      if (!sourceRelativePath || !destRelativePath) {
        return this.returnError({}, "Invalid source or destination path");
      }

      const res = await api.duplicatePath({
        rootPath: root,
        sourceRelativePath,
        destRelativePath,
        overwrite,
      });

      if (!res?.success) {
        return this.returnError({}, res?.error || "Failed to duplicate on disk");
      }

      const type = res.type === "directory" ? "directory" : "file";

      logger.log(
        { tags: "tools|tandem|duplicatefile|info", color1: "lime" },
        `Tandem duplicated ${type}: ${normalizedSourcePath} -> ${normalizedDestPath}`,
        { overwrite }
      );

      return this.returnSuccess(
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          type,
          overwrite,
          copiedCount: type === "directory" ? undefined : 1,
          errorCount: 0,
        },
        type === "directory"
          ? "Directory duplicated successfully"
          : "File duplicated successfully"
      );
    } catch (error) {
      logger.error(
        { tags: "tools|tandem|duplicatefile|error", color1: "red" },
        `Tandem duplicate failed: ${sourcePath} -> ${destinationPath}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }
}
